import { useId, useState } from 'react'
import { t } from '@/shared/lib/i18n'
import { parseInstrumentJumpIndex } from '@/shared/lib/parseInstrumentJumpIndex'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'

interface JumpInstrument {
  id: number
}

interface InstrumentPageJumpProps {
  instruments: JumpInstrument[]
  selectedInstrumentId: number | null
  onInstrumentChange: (instrumentId: string) => void
}

const InstrumentPageJump = ({
  instruments,
  selectedInstrumentId,
  onInstrumentChange,
}: InstrumentPageJumpProps) => {
  const controlId = useId()
  const countHintId = useId()
  const [pageJumpValue, setPageJumpValue] = useState('')

  if (instruments.length <= 1) return null

  // Read the value from the event (not React state) so Enter/blur always see what the user typed.
  const commitPageJump = (rawValue: string) => {
    const pageNum = parseInstrumentJumpIndex(rawValue, instruments.length)
    if (pageNum === null) {
      setPageJumpValue('')
      return
    }
    const targetInstrument = instruments[pageNum - 1]
    if (!targetInstrument) {
      setPageJumpValue('')
      return
    }
    // Already on this instrument — clear the jump field without a redundant reload.
    if (targetInstrument.id === selectedInstrumentId) {
      setPageJumpValue('')
      return
    }
    onInstrumentChange(String(targetInstrument.id))
    setPageJumpValue('')
  }

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={controlId} className="text-xs text-muted-foreground">
        {t('reports.maintenanceLogs.jumpToPage')}
      </Label>
      <div className="flex items-center gap-1">
        <Input
          id={controlId}
          type="number"
          min={1}
          max={instruments.length}
          value={pageJumpValue}
          onChange={(event) => setPageJumpValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commitPageJump(event.currentTarget.value)
            }
          }}
          onBlur={(event) => commitPageJump(event.currentTarget.value)}
          placeholder={t('reports.maintenanceLogs.jumpToPagePlaceholder')}
          aria-describedby={countHintId}
          className="w-20 tabular-nums"
        />
        <span id={countHintId} className="text-xs text-muted-foreground">
          / {instruments.length}
        </span>
      </div>
    </div>
  )
}

export default InstrumentPageJump
